import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import { HEATMAPROOTWIDTH, luminanceScale } from "consts";
import { roundToTwo } from "utils/helpers";
import { CClassEnum, CustomClassData, ILightingClassConfig, MClassEnum, PClassEnum } from "types";
import { useLuminanceData } from "../../hooks/useLuminanceData";
import { getMRoadClassTargets } from "../../utils/road-class";
import { SurfaceWithIlluminance } from "../../types";
import Heatmap from "./Heatmap";
import getStyles from "./styles";

type Props = {
  id: string;
  index: number;
  showLane: boolean;
  luminance: {
    values: number[];
    l_avg: number;
    u_o: number;
    u_l: number;
    f_ti: number;
    rei: number;
    "obs.y": number;
    [key: string]: unknown;
  };
  illuminance: {
    "pts.x": unknown;
    "pts.y": unknown;
    "dim.x": unknown;
    "dim.y": unknown;
  };
  surfaces: SurfaceWithIlluminance[];
  sidewalkPlacing: string | null;
  ulor: number;
  customRoadClass: CustomClassData | undefined;
  roadClass: MClassEnum | CClassEnum | PClassEnum;
  lightingClassConfig: ILightingClassConfig;
};

const LuminanceHeatmap: FC<Props> = ({
  id,
  index,
  showLane,
  luminance,
  illuminance,
  surfaces,
  sidewalkPlacing,
  ulor,
  roadClass,
  customRoadClass,
  lightingClassConfig,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme).LuminanceHeatmap, [theme]);
  const { targetValue, uo_target, ul_target, fti_target, rei_target } = getMRoadClassTargets(lightingClassConfig);
  const { ptsX, ptsY, dimX, dimY, luminanceData, maxLuminanceValue } = useLuminanceData({
    luminance,
    illuminance,
    surfaces,
    sidewalk_placing: sidewalkPlacing,
  });

  return (
    <Box component="section" sx={styles.container}>
      {index === 0 && (
        <Typography variant="h2" sx={styles.title}>
          {t("Luminance calculation field (heatmap diagram):")} <span id="dimX1">{dimX}</span>m &times; <span id="dimY1">{roundToTwo(dimY)}</span>m (
          <span id="ptsX1">{ptsX}</span> &times; <span id="ptsY1">{ptsY}</span> {t("pts")})
        </Typography>
      )}

      <Stack component={Paper} sx={styles.card}>
        <Box>
          {showLane && <Typography variant="h3" sx={styles.label}>{`${t("Lane")} ${index + 1}`}</Typography>}
          {luminanceData.length > 0 && (
            <Heatmap
              id={id}
              width={HEATMAPROOTWIDTH}
              data={luminanceData}
              ptsX={ptsX}
              ptsY={ptsY}
              dimX={dimX}
              dimY={dimY}
              maxValue={maxLuminanceValue}
              maxValue_left={0}
              maxValue_right={0}
              updatedHeight={null}
              disableXAxis={false}
              scale={luminanceScale}
              legendTitle={"Luminance (cd/m²)"}
              heatmapTitle={t("Road")}
              ypos={luminance["obs.y"].toString()}
            />
          )}
        </Box>

        <Table
          padding="none"
          sx={{
            ...styles.table,
            ...(index > 0
              ? {
                  pageBreakBefore: "always",
                  breakBefore: "page",
                  mt: "5mm",
                }
              : {}),
          }}
        >
          <TableHead sx={styles.thead}>
            <TableRow>
              <TableCell> </TableCell>
              <TableCell>{t("y position")}</TableCell>
              <TableCell>
                L̄<sub>m</sub>
              </TableCell>
              <TableCell>
                U<sub>o</sub>
              </TableCell>
              <TableCell>
                U<sub>d</sub>
              </TableCell>
              <TableCell>
                f<sub>TI</sub>
              </TableCell>
              <TableCell>
                R<sub>EI</sub>
              </TableCell>
              <TableCell>
                R<sub>UL</sub>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={styles.tbody}>
            <TableRow>
              <TableCell> </TableCell>
              <TableCell>{roundToTwo(luminance["obs.y"])}</TableCell>
              <TableCell>
                {roundToTwo(luminance.l_avg)} cd/m<sup>2</sup>
              </TableCell>
              <TableCell>{roundToTwo(luminance.u_o)}</TableCell>
              <TableCell>{roundToTwo(luminance.u_l)}</TableCell>
              <TableCell>{roundToTwo(luminance.f_ti)} %</TableCell>
              <TableCell>{luminance.rei === -1.0 ? "-" : roundToTwo(luminance.rei)}</TableCell>
              <TableCell>{Math.round(Number(ulor))}%</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                {t("Target Value")} ({customRoadClass?.class_type ? customRoadClass.class_type : roadClass}):
              </TableCell>
              <TableCell> </TableCell>
              <TableCell>
                {targetValue} cd/m<sup>2</sup>
              </TableCell>
              <TableCell>{uo_target}</TableCell>
              <TableCell>{ul_target}</TableCell>
              <TableCell>{fti_target}</TableCell>
              <TableCell>{rei_target}</TableCell>
              <TableCell>{Math.round(Number(ulor))}%</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Stack>
    </Box>
  );
};

export default LuminanceHeatmap;
